// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, useContext, useMemo, useState } from 'react';

type ModalOpenContextType = {
  register: () => () => void;
};

/** Use `useModalOpenGuardProvider` to create and pass the context value. */
export const ModalOpenGuard = createContext<ModalOpenContextType | undefined>(undefined);

/**
 * Used by a Popover to prevent `closeOnClickOutside` from firing while a nested modal is open.
 * Wrap the Popover in `<ModalOpenGuard value={context}>`
 * and pass `hasOpenModal` to `closeOnClickOutside`.
 *
 * Necessary because Mantine v9 uses React's `Activity` component for `keepMounted` transitions,
 * which deactivates effects in hidden subtrees. If the Popover closes (via `closeOnClickOutside`)
 * while a nested modal is still open, `Activity` goes hidden and the modal's close transition
 * effect is deactivated, leaving the modal permanently stuck open.
 */
export const useModalOpenGuardProvider = () => {
  const [count, setCount] = useState(0);
  const context = useMemo<ModalOpenContextType>(
    () => ({
      register: () => {
        setCount((c) => c + 1);
        return () => setCount((c) => c - 1);
      },
    }),
    [],
  );
  return { hasOpenModal: count > 0, context };
};

/** Used by `WrappedModal` to register itself with a parent `ModalOpenGuard` when open. */
export const useModalOpenGuard = () => useContext(ModalOpenGuard);
